<template>
  <v-card class="invoice_wrapper pa-0 ma-0" flat>
    <WarningBanner :warning-messages="warningMessages">
    </WarningBanner>

    <div class="ma-0 pa-0">
      <template v-if="OrderDetailEditList != null">
        <UpgradedTable :tableStorageKey="'JobInvoiceTable'" :maxHeight="contentHeight - 100" :defaultColumns="columns"
          :baseItemHeight="50" :groupBy="groupByMachine ? 'JobId' : undefined" :allowColumnConfig="true"
          :newRowInitial="newRowDefaults" :showNewRow="!readonly" :loading="loading" :rows="OrderDetailEditList"
          :createRowFunc="createNewRow" :isEditableCellFunc="isEditableCell" ref="UpgradedTable">

          <!-- grouping header -->
          <template v-slot:group-header="{ row, collapseFunc }">
            <GroupByMachineHeader v-if="groupByMachine" :machine="getMachineFromSubJobId(row._groupRows[0].JobId)"
              :collapseFunc="collapseFunc" />
          </template>


          <!-- Article number slot is only editable when adding new line -->
          <template v-slot:header-ArticleNumber>
            <div class="py-1"> <!-- the header slot here is used as an anchor for the header row height -->
              {{ $t('servicesystem.common_objects.order.article') }}
            </div>
          </template>
          <template v-slot:ArticleNumber="{ row, clearNewRow, onNavigate }">


            <EditableCell :row-id="row.Id" prop="ArticleNumber" :readonly="!row.__new" :isActive="() => { }"
              :onEnter="() => { }" :onCancel="() => { }" :onCommit="() => { }" :onNavigate="onNavigate">
              <template #display>
                <template v-if="row.__new">
                  <div style="height: 41px;" class="d-flex align-center justify-center">
                    <article-search-menu-text-field v-model="row.ArticleNumber" :key="searchresetkey" dense
                      class="my-auto" ref="articleinput" :loading="row.loading || row.loading" clearable
                      @selected="(item) => onArticleSelected(item, row)" @clear="clearNewRow">
                    </article-search-menu-text-field>
                  </div>
                </template>
                <div v-else class="nowrapheader ma-0">
                  {{ row.ArticleNumber }}
                </div>
              </template>
            </EditableCell>
          </template>

          <!-- Description -->
          <template v-slot:ArticleDescription="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="ArticleDescription" :readonly="!row.ArticleNumber"
              ref="descriptionCell" :isActive="isCellActive" :onEnter="enterCell" :onCancel="cancelCell"
              :onNavigate="onNavigate" :loading="row.descriptionLoading"
              :error="!!(row.__fieldError && row.__fieldError.ArticleDescription)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'ArticleDescription',
                  save: () => updateArticleDescriptionAction(row, row.ArticleDescription),
                  id, p, commitCell, onNavigate, intent
                })">
              <template #display>
                <v-col class="pa-0">
                  <v-row no-gutters>
                    <div class="nowrapheader"
                      :style="row.StartDateTime && row.StopDateTime ? 'margin-bottom: 6px; margin-top: 7px;' : ''">
                      {{ row.ArticleDescription }}
                    </div>
                  </v-row>
                  <v-row v-if="row.StartDateTime && row.StopDateTime" no-gutters class="mt-n1">
                    <span style="font-size: 11px;">{{ row._startStopText }}</span>
                    <DrivingIndicator v-if="row.StartLatitude && row.StartLongitude" :row="row" />
                    <SamTechIndicator v-if="InvoiceCopy.Samtech" :row="row" :samtechConfig="InvoiceCopy.Samtech" />
                  </v-row>
                </v-col>
              </template>

              <template #edit>
                <v-col class="pa-0">
                  <v-row no-gutters style="padding: 2px;">
                    <QTextField v-model="row.ArticleDescription"
                      :disabled="row.descriptionLoading || row.Deleted || readonly" externaledit singleline
                      :loading="row.loading || row.descriptionLoading" @input="markDirty(row)">
                    </QTextField>
                  </v-row>
                  <v-row v-if="row.StartDateTime && row.StopDateTime" no-gutters class="mt-n1">
                    <span style="font-size: 11px;">{{ row._startStopText }}</span>
                    <DrivingIndicator v-if="row.StartLatitude && row.StartLongitude" :row="row" />
                    <SamTechIndicator v-if="InvoiceCopy.Samtech" :row="row" :samtechConfig="InvoiceCopy.Samtech" />
                  </v-row>
                </v-col>
              </template>
            </EditableCell>
          </template>

          <!-- EmploymentTimeReportDate -->
          <template
            v-slot:EmploymentTimeReportDate="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="EmploymentTimeReportDate" :readonly="!row.ArticleNumber"
              :isActive="isCellActive" :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate"
              :loading="row.employmentTimeReportDateLoading"
              :error="!!(row.__fieldError && row.__fieldError.EmploymentTimeReportDate)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'EmploymentTimeReportDate',
                  save: () => updateEmploymentTimeReportDateAction(row, row.EmploymentTimeReportDate),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row._employmentTimeReportDateText }}</template>

              <template #edit>
                <QTextField :value="oldFormatDate(row.EmploymentTimeReportDate)" style="max-width:95px;"
                  :disabled="row.employmentTimeReportDateLoading || row.Deleted || readonly" reverse type="date"
                  externaledit singleline :loading="row.loading || row.employmentTimeReportDateLoading"
                  @change="val => { row.EmploymentTimeReportDate = val; markDirty(row); }">
                </QTextField>
              </template>
            </EditableCell>
          </template>

          <!-- EmploymentId -->
          <template v-slot:EmploymentId="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="EmploymentId" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.employeeLoading"
              :error="!!(row.__fieldError && row.__fieldError.EmploymentId)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'EmploymentId',
                  save: () => commitEmploymentId(row, row.EmploymentId),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row._employmentName }}</template>

              <template #edit>
                <QAutoComplete v-model="row.EmploymentId" :list="InvoiceCopy.Employees"
                  :disabled="row.employeeLoading || row.Deleted || readonly" :textkey="'Name'" :valuekey="'Id'"
                  :loading="row.loading || row.employeeLoading" externaledit allowexternaloverride singleline
                  @input="() => { markDirty(row); }">
                </QAutoComplete>
              </template>
            </EditableCell>
          </template>

          <!-- Machine job -->
          <template v-slot:JobId="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="JobId" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.moveLoading"
              :error="false" :onCommit="(id, p, intent) => {
                commitCell(id, p);
                if (intent && intent.kind) onNavigate(id, p, { kind: intent.kind, commit: false });
              }">

              <template #display>
                <div v-if="row.ArticleNumber" class="nowrapheader" :class="row.JobId ? '' : 'warning--text'">
                  {{ row._jobName || 'Unlinked' }}
                </div>
              </template>

              <template #edit="{ deactivate }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs">
                      <QAutoComplete :value="row.JobId" :disabled="row.moveLoading || row.Deleted || readonly"
                        :list="JobSelections" :textkey="'MachineDisplayName'" :valuekey="'Id'"
                        :loading="row.loading || row.moveLoading" externaledit allowexternaloverride singleline
                        @input="markDirty(row)"
                        @change="(value) => { selectOrderDetailJobAction(row, row.JobId, value); deactivate(); }" />
                    </div>
                  </template>
                  <span>{{ row._jobName || 'Unlinked lines need to be assigned to a machine.' }}</span>
                </v-tooltip>
              </template>
            </EditableCell>
          </template>

          <!-- Warehouse -->
          <template v-slot:WareHouseId="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="WareHouseId" :readonly="!row.ArticleNumber || row.IsInternal"
              :isActive="isCellActive" :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate"
              :loading="row.warehouseLoading" :error="!!(row.__fieldError && row.__fieldError.WareHouseId)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'WareHouseId',
                  save: () => commitWarehouseId(row, row.WareHouseId),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ row.WareHouseLocation }}
                    </span>
                  </template>
                  <span>
                    {{ row.WareHouseLocation }}
                  </span>
                </v-tooltip>
              </template>

              <template #edit>
                <div v-if="!row.IsInternal">
                  <QAutoComplete v-model="row._selectStore"
                    :disabled="row.warehouseLoading || row.Deleted || readonly" :list="row.StoreItems"
                    :returnobject="true" :textkey="'DisplayName'" :loading="row.loading || row.warehouseLoading"
                    externaledit allowexternaloverride singleline @change="(item) => selectStore(row, item)">
                    <template v-slot:item="{ item }">
                      <div>
                        <v-badge :color="item.Available > 0 ? 'green' : 'error'" inline class="mt-0" dot></v-badge>
                        {{ item.DisplayName }}
                        <span>({{ item.Available }})</span>
                      </div>
                    </template>
                  </QAutoComplete>

                </div>
                <div v-else><!-- not warehoused --></div>
              </template>
            </EditableCell>
          </template>

          <!-- Hidden -->
          <template v-slot:header-HiddenNoInvoice>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" class="my-2">mdi-eye-off</v-icon>
              </template>
              <span>{{ $t('servicesystem.common_objects.order.hidden') }}</span>
            </v-tooltip>
          </template>
          <template v-slot:HiddenNoInvoice="{ row }">
            <v-checkbox v-if="row.ArticleNumber" :input-value="!!row.HiddenNoInvoice"
              :indeterminate="row.HiddenNoInvoice && row.Discount < 100" hide-details dense class="mb-1 pb-1"
              :ripple="false" :disabled="readonly || row.hiddenNoInvoiceLoading"
              :loading="row.hiddenNoInvoiceLoading"
              @change="(val) => { markDirty(row); selectHiddenNoInvoiceAction(row, val); }">
            </v-checkbox>
          </template>

          <!-- No charge -->
          <template v-slot:header-NoDetailInvoice>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" class="my-2">mdi-currency-usd-off</v-icon>
              </template>
              <span>{{ $t('servicesystem.common_objects.order.no_detail_invoice') }}</span>
            </v-tooltip>
          </template>
          <template v-slot:NoDetailInvoice="{ row }">
            <v-checkbox v-if="row.ArticleNumber" :input-value="!!row.NoDetailInvoice" hide-details
              :disabled="readonly || row.noDetailInvoiceLoading" dense class="mb-1 pb-1" :ripple="false"
              :loading="row.noDetailInvoiceLoading"
              @change="(val) => { markDirty(row); selectNoDetailInvoiceAction(row, val); }" />
          </template>

          <!-- Count -->
          <template v-slot:ArticleCount="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="ArticleCount" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.articleCountLoading"
              :error="!!(row.__fieldError && row.__fieldError.ArticleCount)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'ArticleCount',
                  save: () => updateArticleCountAction(row, row.ArticleCount),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row._countText }}</template>

              <template #edit>
                <QTextField v-model="row.ArticleCount" :rules="rules.ArticleCount"
                  :disabled="row.articleCountLoading || row.Deleted || readonly" class="numberfield" reverse
                  externaledit allowexternaloverride :loading="row.articleCountLoading" singleline
                  @input="markDirty(row)">
                </QTextField>
              </template>
            </EditableCell>
          </template>

          <!-- Discount -->
          <template v-slot:Discount="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="Discount" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.discountLoading"
              :error="!!(row.__fieldError && row.__fieldError.Discount)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'Discount',
                  save: () => updateDiscountAction(row, row.Discount),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row.Discount ? row.Discount + ' %' : '' }}</template>

              <template #edit>
                <QTextField v-model="row.Discount" :disabled="row.discountLoading || row.Deleted || readonly"
                  :rules="rules.Discount" class="numberfield" reverse externaledit allowexternaloverride prefix="%"
                  :loading="row.loading || row.discountLoading" singleline @input="markDirty(row)">
                </QTextField>
              </template>
            </EditableCell>
          </template>

          <!-- Unit price -->
          <template v-slot:ArticlePrice="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="ArticlePrice" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.articlePriceLoading"
              :error="!!(row.__fieldError && row.__fieldError.ArticlePrice)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'ArticlePrice',
                  save: () => commitPrice(row, row.ArticlePrice),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row._priceText }}</template>

              <template #edit>
                <QTextField v-model="row.ArticlePrice"
                  :disabled="row.articlePriceLoading || row.Deleted || readonly" reverse class="numberfield"
                  externaledit :loading="row.loading || row.articlePriceLoading" singleline allowexternaloverride
                  @input="markDirty(row)">
                </QTextField>
              </template>
            </EditableCell>
          </template>

          <!-- Unit price in -->
          <template v-slot:ArticlePriceIn="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="ArticlePriceIn" :readonly="!row.ArticleNumber" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :loading="row.articlePriceInLoading"
              :error="!!(row.__fieldError && row.__fieldError.ArticlePriceIn)" :onCommit="(id, p, intent) =>
                commitField({
                  row,
                  prop: 'ArticlePriceIn',
                  save: () => updateArticlePriceInAction(row, row.ArticlePriceIn),
                  id, p, commitCell, onNavigate, intent
                })">

              <template #display>{{ row._priceInText }}</template>

              <template #edit>
                <QTextField v-model="row.ArticlePriceIn"
                  :disabled="row.articlePriceInLoading || row.Deleted || readonly" reverse class="numberfield"
                  externaledit :loading="row.loading || row.articlePriceInLoading" singleline allowexternaloverride
                  @input="markDirty(row)">
                </QTextField>
              </template>
            </EditableCell>
          </template>

          <!-- Sum / Total (readonly) -->
          <template v-slot:TotalAmount="{ row, isCellActive, enterCell, commitCell, cancelCell, onNavigate }">
            <EditableCell :row-id="row.Id" prop="TotalAmount" :readonly="true" :isActive="isCellActive"
              :onEnter="enterCell" :onCancel="cancelCell" :onNavigate="onNavigate" :onCommit="commitCell">

              <template #display>{{ row.ArticleNumber ? getTotal(row).toFixed(2) : '' }}</template>

            </EditableCell>
          </template>

          <!-- Action slot -->
          <template v-slot:Actions="{ row }">
            <div class="ml-n1" style="position:relative; overflow: visible;">
              <v-btn v-if="!readonly && row.ReadOnly !== true" :loading="row.loading || row.deleteLoading"
                :disabled="row.deleteLoading" icon @click="deleteDetailAction(row)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-icon v-else style="opacity: 0.5;">mdi-shield-lock</v-icon>
            </div>
          </template>

          <!-- New row create button -->
          <template v-slot:new-btn="{ row, doCreateNew }">
            <v-btn v-show="row.ArticleNumber" :loading="createLoading || row.loading" small class="px-0"
              :disabled="createLoading || !validateNewRow(row)" color="primary" style="min-width: 30px;"
              @click="doCreateNew()">
              <v-icon size="20">mdi-plus</v-icon>
            </v-btn>
          </template>

        </UpgradedTable>
      </template>

      <v-progress-linear v-else-if="InvoiceCopy !== false" indeterminate />
    </div>

    <!-- order total -->
    <v-divider v-if="OrderDetailEditList" />
    <v-list-item :class="['px-0', contributionMarginWarning ? 'invoiceWarning' : 'invoiceSuccess']"
      v-if="OrderDetailEditList" dense>
      <v-container class="pa-4" fluid>
        <v-row no-gutters>
          <v-col cols="0">
            <v-btn small outlined @click="timeSummaryTrigger++" tabindex="-1">Time summary</v-btn>
            <TimeSummaryDialog
              :orderDetails="OrderDetailEditList.filter(item => item.Type === 'Drive' || item.Type === 'Work')"
              :employmentMap="employmentMap" :externaltrigger="timeSummaryTrigger" />
            <v-alert v-if="lastError" type="error" max-height="35" dense
              class="d-flex align-center my-auto ml-4 nowrapheader">
              {{ lastError }}
            </v-alert>
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <v-row no-gutters>
              <v-col class="mr-7">
                <v-list-item-subtitle class="numberfield2 text-right mb-4"
                  v-text="$t('servicesystem.common_objects.order.total_discount')" />
                <v-list-item-title class="numberfield2 text-right mb-2"
                  v-text="$t('servicesystem.common_objects.order.total_sum')" />
                <v-list-item-title class="numberfield2 text-right"
                  v-text="$t('servicesystem.common_objects.order.contrib')" />
              </v-col>
              <v-col>
                <v-list-item-subtitle class="numberfield2 text-right font-weight-regular mb-4" v-text="TotalDiscount" />
                <v-list-item-title class="numberfield2 text-right font-weight-regular mb-2"
                  v-text="TotalAmount.toFixed(2)" />
                <v-list-item-title
                  :class="['numberfield2', 'text-right', 'font-weight-regular', contributionMarginWarning ? 'warning' : '']"
                  v-text="`${ContributionMargin.toFixed(2)} ${ContributionMarginInPercentage}`" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-list-item>
  </v-card>
</template>

<script>
import SRConst from "@/helpers/Constants/SignalRConstants.js";
import ClientAPIMixin from "@/api/ClientAPIMixin";
import client from "@/api/client";
import ArticleSearchMenuTextField from "@/components/ArticleSearchMenuTextField.vue";
import UpgradedTable from "@/views/mypage/UpgradedTable.vue";
import GroupByMachineHeader from "./GroupByMachineHeader.vue";
import DrivingIndicator from "./DrivingIndicator.vue";
import SamTechIndicator from "./SamTechIndicator.vue";
import TimeSummaryDialog from "@/components/Dialogs/TimeSummaryDialog.vue";
import EditableCell from "@/views/mypage/EditableCell.vue";
import WarningBanner from "@/components/WarningBanner.vue";

const loadingKeys = Object.freeze({
  loading: false,
  articlePriceLoading: false,
  articlePriceInLoading: false,
  articleCountLoading: false,
  moveLoading: false,
  employmentTimeReportDateLoading: false,
  employeeLoading: false,
  warehouseLoading: false,
  descriptionLoading: false,
  deleteLoading: false,
  priceLoading: false,
  discountLoading: false,
  hiddenNoInvoiceLoading: false,
  noDetailInvoiceLoading: false,
});

export default {
  name: "OrderLinesCard",
  props: {
    jobId: { type: Number, default: null },
    groupByMachine: Boolean,
    disabled: Boolean
  },
  components: {
    ArticleSearchMenuTextField,
    UpgradedTable,
    GroupByMachineHeader,
    DrivingIndicator,
    SamTechIndicator,
    TimeSummaryDialog,
    EditableCell,
    WarningBanner,
  },
  mixins: [ClientAPIMixin],

  data() {
    return {
      groupBy: null,
      columns: {
        ArticleNumber: { prop: 'ArticleNumber', label: this.$t('servicesystem.common_objects.order.article'), show: 'always', width: 150, minWidth: 150 },
        ArticleDescription: { prop: 'ArticleDescription', label: this.$t('servicesystem.common_objects.order.description'), show: true, minWidth: 250 },
        JobId: { prop: 'JobId', label: this.$t('servicesystem.common_objects.machine.machine'), show: true, minWidth: 150 },
        HiddenNoInvoice: { prop: 'HiddenNoInvoice', label: this.$t('servicesystem.common_objects.order.hidden'), show: true, width: 45, align: 'center' },
        NoDetailInvoice: { prop: 'NoDetailInvoice', label: this.$t('servicesystem.common_objects.order.no_detail_invoice'), show: true, width: 45, align: 'center' },
        EmploymentId: { prop: 'EmploymentId', label: this.$t('servicesystem.common_objects.order.employee'), show: true, minWidth: 100 },
        EmploymentTimeReportDate: { prop: 'EmploymentTimeReportDate', label: this.$t('servicesystem.common_objects.order.date'), show: true, width: 110 },
        WareHouseId: { prop: 'WareHouseId', label: this.$t('servicesystem.common_objects.order.warehouse'), show: true, width: 100 },
        ArticleCount: { prop: 'ArticleCount', label: this.$t('servicesystem.common_objects.order.count'), show: true, width: 58, align: 'right' },
        Discount: { prop: 'Discount', label: this.$t('servicesystem.common_objects.order.discount'), show: true, width: 60, align: 'right' },
        ArticlePrice: { prop: 'ArticlePrice', label: this.$t('servicesystem.common_objects.order.price'), show: true, minWidth: 70, align: 'right' },
        ArticlePriceIn: { prop: 'ArticlePriceIn', label: this.$t('servicesystem.common_objects.order.pricein'), show: this.showPriceIn, minWidth: 70, align: 'right' },
        TotalAmount: { prop: 'TotalAmount', label: this.$t('servicesystem.common_objects.order.sum'), show: true, minWidth: 70, align: 'right' },
        Actions: { prop: 'Actions', label: this.$t('servicesystem.common_objects.commonterms.actions'), show: 'always', minWidth: 45, width: 45, align: 'center', fixed: 'right' },
      },

      lastError: null,
      Job: null,
      loading: false,
      warehouses: [],
      employmentMap: new Map(),
      jobMap: new Map(),
      InvoiceCopy: null,
      OrderDetailEditList: null,
      searchresetkey: 1,

      createLoading: false,

      newRowDefaults: {
        ArticleNumber: '',
        ArticleDescription: '',
        EmploymentTimeReportDate: null,
        EmploymentId: null,
        JobId: null,
        WareHouseId: null,
        WareHouseLocation: '',
        StoreLocationId: '',
        ArticleCount: 1,
        Discount: 0,
        ArticlePrice: 0,
        ArticlePriceIn: 0,
        TotalAmountExclVat: 0,
        ...loadingKeys,
      },

      timeSummaryTrigger: 1,
    };
  },

  mounted() {
    this.$store.dispatch("NotificationStore/subscribe", [SRConst.EventKeys.OrderChange, this.orderChanged]);
    this.$store.dispatch("NotificationStore/subscribe", [SRConst.EventKeys.JobChange, this.jobChanged]);
    this.init();
  },

  beforeDestroy() {
    this.$store.dispatch("NotificationStore/unsubscribe", [SRConst.EventKeys.OrderChange, this.orderChanged]);
    this.$store.dispatch("NotificationStore/unsubscribe", [SRConst.EventKeys.JobChange, this.jobChanged]);
  },

  methods: {
    selectStore(row, item) {
      if (item == null) {
        return;
      }
      row.WareHouseLocation = item.DisplayName;
      row.WareHouseId = item.StoreId;
      row.StoreLocationId = item.StoreLocationId;
    },
    ensureFieldMaps(item) {
      if (!item.__fieldError) this.$set(item, '__fieldError', {});
    },

    async commitField({ save, id, p, commitCell, onNavigate, intent }) {
      // kick off the save but don't await it
      const saving = Promise.resolve(save());

      // optimistically close editor & navigate now
      commitCell(id, p);
      if (intent && intent.kind) onNavigate(id, p, { kind: intent.kind, commit: false });

      // Optional: surface failures (editor stays closed; cell shows error outline)
      saving.catch(() => { /* lastError already set in saveOrderDetail */ });
    },

    /* === NEW: central editability rule used by UpgradedTable for nav/click === */
    isEditableCell(row, prop) {
      if (!row || row.__group) return false;
      if (this.readonly || row.Deleted) return false;

      // read-only columns
      const readOnly = new Set(['TotalAmount', 'Actions']);
      if (readOnly.has(prop)) return false;

      // checkbox / toggles are not "cell editors" for keyboard nav
      const checkboxLike = new Set(['HiddenNoInvoice', 'NoDetailInvoice']);
      if (checkboxLike.has(prop)) return false;

      // ArticleNumber editable only on the placeholder new row
      if (prop === 'ArticleNumber') return !!row.__new;

      // respect PriceIn visibility setting
      if (prop === 'ArticlePriceIn' && !this.showPriceIn) return false;

      // non-warehoused articles have no warehouse editor
      if (prop === 'WareHouseId' && row.IsInternal) return false;

      // all other inputs require an ArticleNumber picked
      const needsArticle = new Set([
        'ArticleDescription', 'EmploymentTimeReportDate', 'EmploymentId',
        'JobId', 'WareHouseId', 'ArticleCount', 'Discount',
        'ArticlePrice', 'ArticlePriceIn'
      ]);
      if (needsArticle.has(prop)) return !!row.ArticleNumber;

      return false;
    },

    init() {
      this.getInvoice().then(() => {
        this.reset();
        this.lastError = null;
      });
      this.getWarehouses();
      this.getAllEmployees();
    },

    async getAllEmployees() {
      var empList = await this.getEmploymentList();
      this.employmentMap = new Map(empList.map(obj => [obj.Id, obj]));
    },

    getEmploymentName(id) {
      if (this.employmentMap.has(id)) {
        const emp = this.employmentMap.get(id);
        return emp.Name;
      }
      return "";
    },

    jobChanged(item) {
      if (
        ((this.InvoiceCopy && this.InvoiceCopy.Jobs.find((e) => e.Id == item.jobId)) ||
          this.InvoiceCopy.Primary.Id == item.jobId) &&
        item.className == "JobApprovedEventArgs"
      ) {
        this.getInvoice().then(() => { this.reset(); });
      }
    },

    orderChanged(item) {
      if (
        (this.InvoiceCopy && this.InvoiceCopy.Jobs.find((e) => e.OrderId == item.orderId)) ||
        this.InvoiceCopy.Primary.OrderId == item.orderId
      ) {
        this.getInvoice().then(() => { this.reset(); });
      }
    },

    validateNewRow(row) {
      var result = true;
      if (row.loading) return false;
      for (const [key, rules] of Object.entries(this.rules)) {
        const value = row[key];
        for (const rule of rules) {
          const ruleResult = rule(value);
          if (ruleResult !== true) { result = false; break; }
        }
      }
      return result;
    },

    markDirty(row) { row.dirty = true; },

    beginEdit(row) { this.$set(row, 'editing', true); },
    endEdit(row) { this.$set(row, 'editing', false); },

    isEditing(row, hovered) {
      const active = !!(row.editing || row.focus || row.dirty);
      if (active) return true;
      return !!hovered;
    },

    setFocus(row, isFocused) { this.$set(row, 'focus', !!isFocused); },

    getMachineFromSubJobId(id) {
      if (!id) return null;
      var childjob = this.$store.getters["JobHub/JobSubjobs/ChildJob"](id);
      return childjob?.Machine;
    },

    selectOrderDetailJobAction(item, previousValue, value) {
      if (item.__new) {
        item.JobId = value;
        return;
      } else {
        item.moveLoading = true;
        const payload = { Id: item.Id, JobId: value };
        const method = previousValue == null ? this.linkInvoiceDetail : this.moveInvoiceDetail;
        method(payload)
          .then((response) => {
            var formatted = this.formatCleanRow(response);
            Object.assign(item, formatted);
          })
          .catch(() => { this.reset(); })
          .finally(() => { item.moveLoading = false; });
      }
    },

    async saveOrderDetail(item, payloadContent, loadingProp, propName) {
      if (item.__new) {
        const clone = JSON.parse(JSON.stringify(item));
        Object.assign(clone, payloadContent);
        this.recomputeNewRow(clone);
        Object.assign(item, this.formatCleanRow(clone));
        return true;
      }

      this.ensureFieldMaps(item);
      if (!item.__ver) this.$set(item, '__ver', {});              // <-- per-field version bucket
      const myVer = (item.__ver[propName] || 0) + 1;
      this.$set(item.__ver, propName, myVer);

      this.$set(item, loadingProp, true);
      this.$set(item.__fieldError, propName, false);
      item.error = false;

      if (item.JobId == null) item.JobId = this.jobId || this.$route.params.jobId;
      const payload = { JobId: item.JobId, Id: item.Id, Content: payloadContent };

      try {
        const response = await this.changeInvoiceDetail(payload);

        // Only apply if this is still the latest save for this field
        if (item.__ver[propName] === myVer) {
          Object.assign(item, this.formatCleanRow(response));
          this.$set(item, loadingProp, false);
        }
        return true;
      } catch (response) {
        // Only surface error if still the latest attempt
        if (item.__ver[propName] === myVer) {
          console.error(response);
          item.error = true;
          this.lastError = response?.data?.Message || response?.data || 'Save failed';
          this.$set(item, loadingProp, false);
          this.$set(item.__fieldError, propName, true);
          item.dirty = true; // allow re-try
        }
        return false;
      }
    },

    selectHiddenNoInvoiceAction(item, value) {
      const payloadContent = { HiddenNoInvoice: value, Discount: value ? 100 : 0 };
      this.saveOrderDetail(item, payloadContent, 'hiddenNoInvoiceLoading');
    },

    selectNoDetailInvoiceAction(item, value) {
      const payloadContent = { NoDetailInvoice: value ? item.NoDetailInvoice : false, Discount: value ? 100 : 0 };
      this.saveOrderDetail(item, payloadContent, 'noDetailInvoiceLoading');
    },

    commitEmploymentId(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { EmploymentId: value };
      return this.saveOrderDetail(item, payloadContent, 'employeeLoading', 'EmploymentId');
    },

    commitWarehouseId(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { WareHouseId: value };
      return this.saveOrderDetail(item, payloadContent, 'warehouseLoading', 'WareHouseId');
    },

    updateArticleCountAction(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { ArticleCount: this.parseCommaSafeDecimalField(value) };
      return this.saveOrderDetail(item, payloadContent, 'articleCountLoading', 'ArticleCount');
    },

    commitPrice(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { ArticlePrice: this.parseCommaSafeDecimalField(value) };
      return this.saveOrderDetail(item, payloadContent, 'articlePriceLoading', 'ArticlePrice');
    },

    updateArticlePriceInAction(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { ArticlePriceIn: this.parseCommaSafeDecimalField(value) };
      return this.saveOrderDetail(item, payloadContent, 'articlePriceInLoading', 'ArticlePriceIn');
    },

    updateDiscountAction(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { Discount: this.parseCommaSafeDecimalField(value) };
      return this.saveOrderDetail(item, payloadContent, 'discountLoading', 'Discount');
    },

    updateArticleDescriptionAction(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { ArticleDescription: value };
      return this.saveOrderDetail(item, payloadContent, 'descriptionLoading', 'ArticleDescription');
    },

    updateEmploymentTimeReportDateAction(item, value) {
      if (!item.dirty) return false;
      const payloadContent = { EmploymentTimeReportDate: value };
      return this.saveOrderDetail(item, payloadContent, 'employmentTimeReportDateLoading', 'EmploymentTimeReportDate');
    },

    deleteDetailAction(item) {
      item.deleteLoading = true;
      this.deleteInvoiceDetail(item)
        .then(() => {
          const index = this.OrderDetailEditList.findIndex((e) => e.Id == item.Id);
          if (index >= 0) this.OrderDetailEditList.splice(index, 1);
        })
        .finally(() => { item.deleteLoading = false; });
    },

    getInvoice() {
      const jobId = this.jobId || this.$route.params.jobId;
      if (jobId > 0) {
        return ClientAPIMixin.methods.getInvoiceView(jobId).then(data => {
          this.InvoiceCopy = data;
          this.jobMap = new Map(data.Jobs.map(obj => [obj.Id, obj]));
        });
      } else {
        this.dispatch("ErrorStore/add", "Could not find a job id in route");
      }
    },

    getTotal(item) {
      const n = (x) => x ? (this.parseCommaSafeDecimalField(x) || 0) : 0;
      const count = n(item.ArticleCount);
      const price = n(item.ArticlePrice);
      const discount = n(item.Discount);
      const sum = price * count;
      const totalDiscount = sum * (discount / 100);
      return sum - totalDiscount;
    },

    recomputeNewRow(item) {
      item.TotalAmountExclVat = this.getTotal(item);
    },

    cleanInteractions() {
      return { editing: false, dirty: false, focus: false, loading: false, ...loadingKeys };
    },

    computePlaceholders(item) {
      const text = item.StartDateTime ? `${this.i18nFormatDateTime(item.StartDateTime)} - ${item.StopDateTime ? this.i18nFormatTime(item.StopDateTime) : '...'}` : '';
      const empDateText = item.EmploymentTimeReportDate ? this.i18nFormatDate(item.EmploymentTimeReportDate) : '';
      const empName = item.EmploymentId ? this.getEmploymentName(item.EmploymentId) : '';
      const jobName = item.JobId ? this.jobDisplayName(item.JobId) : '';
      const wareHouseName = item.WareHouseId ? this.warehouses.find(w => w.Id === item.WareHouseId)?.Name : '';

      return {
        _startStopText: item.StartDateTime ? text : '',
        _employmentTimeReportDateText: empDateText,
        _employmentName: empName,
        _jobName: jobName,
        _warehouseName: wareHouseName,
        _selectStore: {},
        _countText: item.ArticleCount != null ? Number(item.ArticleCount).toFixed(2) : '',
        _priceText: item.ArticlePrice != null ? Number(item.ArticlePrice).toFixed(2) : '',
        _priceInText: item.ArticlePriceIn != null ? Number(item.ArticlePriceIn).toFixed(2) : '',
        _totalText: item.TotalAmountExclVat != null ? this.getTotal(item).toFixed(2) : '',
      };
    },

    formatCleanRow(item) {
      return { ...this.cleanInteractions(), ...item, ...this.computePlaceholders(item), NoDetailInvoice: item.Discount === 100 };
    },

    reset() {
      this.lastError = null;
      const cleanfunc = (x) => this.formatCleanRow(x);
      this.OrderDetailEditList = this.InvoiceCopy.OrderDetails.map(cleanfunc);
    },

    async getWarehouses() { this.warehouses = await this.getAllWarehouses(); },

    jobDisplayName(jobId) {
      if (jobId != null) {
        const job = this.jobMap.get(jobId);
        return job?.DisplayName;
      } else {
        return "";
      }
    },

    async onArticleSelected(item, newRow) {
      const articleNumber = typeof item === 'string' ? item : (item && item.ArticleNumber);
      if (!articleNumber) return;

      const defaultJobId = this.InvoiceCopy.Jobs[0].Id;
      try {
        const data = await ClientAPIMixin.methods.getArticleAsInvoiceDetail(articleNumber, defaultJobId);
        const merged = {
          ...data,
          EmploymentId: this.InvoiceCopy.DefaultEmploymentId,
          WareHouseId: data.IsInternal ? null : this.InvoiceCopy.DefaultStoreId,
        };
        this.recomputeNewRow(merged);
        Object.assign(newRow, this.formatCleanRow(merged), { dirty: true });
        this.$nextTick(() => this.$refs.descriptionCell.focus());
      } catch (err) {
        console.error("Could not find article", err);
      }
    },

    async createNewRow(newRow) {
      newRow.loading = true;
      this.createLoading = true;
      newRow.ArticleCount = newRow.ArticleCount ? this.parseCommaSafeDecimalField(newRow.ArticleCount) : 0;
      newRow.Discount = newRow.Discount ? this.parseCommaSafeDecimalField(newRow.Discount) : 0;
      newRow.ArticlePrice = newRow.ArticlePrice ? this.parseCommaSafeDecimalField(newRow.ArticlePrice) : 0;
      newRow.ArticlePriceIn = newRow.ArticlePriceIn ? this.parseCommaSafeDecimalField(newRow.ArticlePriceIn) : 0;

      return await client()
        .post("api2/serviceprovider/invoice/detail/", newRow)
        .then((response) => {
          newRow.__new = false;
          var formatted = this.formatCleanRow(response.data);
          Object.assign(newRow, formatted);
          this.InvoiceCopy.OrderDetails.push(newRow);
          this.OrderDetailEditList.push(newRow);
        })
        .catch(e => {
          this.$store.dispatch("triggerevents/responseSnackbar", {
            response: e,
            method: "addNewArticle",
            text: "Error",
            input: newRow,
          });
        })
        .finally(() => {
          this.createLoading = false;
          this.searchresetkey++;
          setTimeout(() => {
            this.$refs.articleinput.focus();
          }, 100)
        });
    },
  },

  computed: {
    contentHeight() { return this.$store.getters['navstore/ContentContainerHeight']; },

    rules() {
      return {
        ArticleCount: [v => this.parseCommaSafeDecimalField(String(v)) <= 9999 || 'Max 9999'],
        Discount: [v => this.parseCommaSafeDecimalField(String(v)) <= 100 || 'Max 100'],
      };
    },

    warningMessages() {
      const messages = [];
      // Specifically show info when order is locked and job is unfinished.
      if (this.OrderLocked && !this.JobLocked) {
        messages.push({
          severity: 1,
          message: this.$t('servicesystem.common_objects.order.order_locked_by_order_status', { skey: this.InvoiceCopy.OrderStatus }),
        });
      }
      return messages;
    },

    contributionMarginWarning() {
      if (!this.$store.state.GlobalSettings) return false;
      const cmPercent = (this.ContributionMargin / this.TotalAmount) * 100;
      return !!(this.$store.state.GlobalSettings.ContributionMarginWarningIfBelow &&
        this.$store.state.GlobalSettings.ContributionMarginWarningIfBelow > cmPercent);
    },

    OrderLocked() {
      return !this.InvoiceCopy || this.InvoiceCopy.OrderLocked;
    },

    JobLocked() {
      return !this.InvoiceCopy || this.InvoiceCopy.JobLocked;
    },

    readonly() {
      return this.disabled || this.OrderLocked || this.JobLocked;
    },

    TotalDiscount() {
      if (!this.OrderDetailEditList) return;
      const val = this.OrderDetailEditList.reduce((acc, curr) => {
        if (curr.Deleted) return acc;
        return acc + curr.TotalAmountExclVat - curr.TotalDiscount;
      }, 0);
      const valNoDiscount = this.OrderDetailEditList.reduce((acc, curr) => {
        if (curr.Deleted) return acc;
        return acc + curr.ArticleCount * curr.ArticlePrice;
      }, 0);
      return (valNoDiscount - val).toFixed(2);
    },

    TotalAmount() {
      if (!this.OrderDetailEditList) return 0;
      return this.OrderDetailEditList.reduce((acc, curr) => {
        if (curr.Deleted) return acc;
        return acc + (curr.ArticlePrice * curr.ArticleCount - curr.TotalDiscount);
      }, 0);
    },

    TotalAmountPriceIn() {
      if (!this.OrderDetailEditList) return;
      const val = this.OrderDetailEditList.reduce((acc, curr) => {
        if (curr.Deleted) return acc;
        return acc + (curr.ArticlePriceIn * curr.ArticleCount);
      }, 0);
      return val.toFixed(2);
    },

    ContributionMarginInPercentage() {
      const ma = this.ContributionMargin / this.TotalAmount;
      if (!isNaN(ma) && isFinite(ma)) return `(${(Math.round(((ma) * 100) * 100) / 100)} %)`;
      return "";
    },

    ContributionMargin() {
      if (!this.OrderDetailEditList) return;
      return this.TotalAmount - this.TotalAmountPriceIn;
    },

    JobSelections() {
      if (!this.InvoiceCopy) return [];
      const selections = this.InvoiceCopy.Jobs.map((e) => ({ disabled: false, ...e }));
      selections.push({ Id: null, DisplayName: "Unlinked", MachineDisplayName: "Unlinked", disabled: true });
      return selections;
    },

    WarehouseSelections() {
      return this.warehouses.map((e) => ({ Name: e.Name, Id: e.Id.toString() }));
    },

    showPriceIn() {
      return this.$store.state.GlobalSettings.InvoiceEnablePriceInField;
    },
  },
};
</script>

<style lang="scss" scoped>
.roundedbottom {
  z-index: 1;
  margin-bottom: -6px;
  border-radius: 0px !important;
  border-bottom-left-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
  border-bottom: 1px solid rgb(202, 202, 202);
}

.nowrap {
  white-space: nowrap;
}
</style>

<style>
.invoice_orderdetail_error {
  background-color: var(--v-error-lighten5) !important;
}

#invoicetable .v-data-table__wrapper table tbody tr:nth-child(odd):not(.ignoreodd) {
  background-color: rgb(245, 249, 255);
}

.text-start {
  padding: 0 8px !important;
}

tr .text-start:first-child,
tr .text-start:last-child {
  padding: 0 16px !important;
}

.job_select_item {
  min-height: 30px !important;
  max-width: 400px !important;
}

.v-data-table__empty-wrapper {
  display: none !important;
}

.numberfield {
  font-family: arial !important;
  font-size: 12px !important;
}

.numberfield2 {
  font-family: arial !important;
  font-size: 14px !important;
}

.deactivated:not(:hover) {
  color: var(--v-disabled-darken2) !important;
  text-decoration: line-through !important;
}

.el-table {
  color: unset;
}

.el-table thead {
  color: unset;
}
</style>